import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { motion } from 'framer-motion';
import { 
  Building2, UserRound, Phone, MapPin, Clock, Star, 
  CheckCircle2, Sparkles, Stethoscope, Play, ChevronDown, Check,
  Activity, HeartPulse, Pill, Brain, Bone, Eye, Award, ArrowRight, Video, MessageSquareQuote, HelpCircle, ShieldCheck
} from 'lucide-react';

import hospitalApiService from '../../services/hospitalApiService';
import VideoConsultModal from '../../components/VideoConsultModal';
import { mediaUrl } from '../../config';

// ─── Color tokens (Vercel / Neo-Minimalist Light) ───────────────────────────
const C = {
  navy:    '#0A0A0A',
  bg:      '#FAFAFA',
  border:  'rgba(0, 0, 0, 0.08)',
  text:    '#171717',
  text2:   '#525252',
  text3:   '#A3A3A3',
  teal:    '#2563EB', // Primary Blue
  tealLt:  '#EFF6FF',
  white:   '#FFFFFF',
  gold:    '#F59E0B',
  goldLt:  '#FFFBEB'
};

const fadeUp: any = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger: any = { visible: { transition: { staggerChildren: 0.1 } } };

// ─── Types ──────────────────────────────────────────────────────────────────
interface TestimonialSample { quote: string; name: string; age?: number; procedure?: string; facility?: string; star_rating?: number; visit_date?: string; is_pinned?: boolean; verified?: boolean; }
interface AccredItem { id: number; name: string; type?: string | null; description?: string; emoji?: string | null; active: boolean; }
interface ProcedureItem { id: number; name: string; emoji?: string; min_price?: number; max_price?: number; description?: string; is_featured?: boolean; }
interface DoctorProfile { id: number; name: string; qualifications?: string; specialty?: string; years_exp?: number; surgeries?: string; rating?: number; photo_url?: string; is_featured_video?: string; }
interface FaqItem { id: number; question: string; answer: string; }
interface TrustSignal { id: number; type: string; name: string; description?: string; emoji?: string; active: boolean; display_order: number; }
interface ProofNumber { value: string; label: string; }
interface VideoItem { id: number; title: string; video_type: string; video_url: string; duration?: string; description?: string; }
interface MediaItem { id: number; media_type: string; media_url: string; caption?: string | null; section_id?: string; display_order?: number; }
interface ConditionTreatment { id: number; name: string; speciality_id?: number; speciality_name?: string; }
interface CareCoordinator { id: number; name: string; role?: string | null; emoji?: string | null; phone?: string | null; availability?: string | null; }
interface VisibilityToggles { show_procedure_videos?: boolean; show_tech_block?: boolean; show_finance_section?: boolean; show_all_3_doctors?: boolean; inherit_accreditations?: boolean; }
interface DeptTemplateData {
  hospital: { name?: string; tagline?: string; address?: string; phone?: string; whatsapp?: string; hours_opd?: string; hours_emergency?: string; established_year?: number; cta_text?: string; };
  dept?: { name: string; emoji?: string; };
  procedures?: ProcedureItem[];
  proof_numbers?: ProofNumber[];
  testimonials?: { total: number; average_rating?: number; samples?: TestimonialSample[]; list?: TestimonialSample[]; items?: TestimonialSample[]; };
  accreditations?: AccredItem[];
  faqs?: { total: number; list?: FaqItem[]; faq_list?: FaqItem[]; items?: FaqItem[]; };
  featured_doctor?: DoctorProfile;
  videos?: { total: number; video_list?: VideoItem[]; list?: VideoItem[] };
  video_list?: VideoItem[];
  media?: { total: number; items?: MediaItem[]; };
  conditions_treatments?: ConditionTreatment[];
  dept_id?: number;
  care_coordinator?: CareCoordinator | null;
  visibility_toggles?: VisibilityToggles;
  settings: any;
  trust_signals?: any;
  trust_signal_list?: TrustSignal[];
  all_testimonials?: TestimonialSample[];
  testimonial_list?: TestimonialSample[];
  faq_list?: FaqItem[];
}

// Media/uploads are served by the API host (e.g. http://localhost:8010/uploads/..).
// Absolute URLs from the API are passed through untouched.
const mediaSrc = (url: string) => (/^https?:\/\//.test(url) ? url : mediaUrl(url));

const fontStyles = (
  <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');
    body { font-family: 'Inter', sans-serif; background-color: ${C.bg}; color: ${C.text}; }
    h1, h2, h3, h4, h5, h6 { font-family: 'Plus Jakarta Sans', sans-serif; letter-spacing: -0.02em; }
    .glass-panel { background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(20px); border: 1px solid ${C.border}; }
  `} />
);

const getIcon = (emoji: string) => {
  if (emoji?.includes('🏥')) return <Building2 size={24} />;
  if (emoji?.includes('❤️')) return <HeartPulse size={24} />;
  if (emoji?.includes('🧠')) return <Brain size={24} />;
  if (emoji?.includes('🦴')) return <Bone size={24} />;
  if (emoji?.includes('👁')) return <Eye size={24} />;
  if (emoji?.includes('💊')) return <Pill size={24} />;
  if (emoji?.includes('👨‍⚕️')) return <Stethoscope size={24} />;
  return <Activity size={24} />;
};

function TrustBar() {
  return (
    <Box component={motion.div as any} initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} sx={{ bgcolor: 'rgba(255, 255, 255, 0.85)', borderBottom: `1px solid ${C.border}`, backdropFilter: 'blur(12px)', px: { xs: 2, md: 3 }, py: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.6, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 22, height: 22, bgcolor: C.navy, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Sparkles size={12} color="white" /></Box>
        <Typography sx={{ fontSize: 12, color: C.text2, fontWeight: 500 }}>Powered by <Box component="strong" sx={{ color: C.navy, fontWeight: 700 }}>Unofy Trust Link</Box></Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', bgcolor: C.tealLt, border: `1px solid rgba(37, 99, 235, 0.2)`, borderRadius: '999px', px: '12px', py: '4px', fontSize: 10, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.05em' }}><CheckCircle2 size={12} /> Verified Profile</Box>
    </Box>
  );
}

function HeroSection({ data, whatsappUrl, onVideoCall }: { data: DeptTemplateData; whatsappUrl: string; onVideoCall: () => void }) {
  const { hospital, dept, settings, testimonials, proof_numbers, procedures } = data;
  const deptName = dept?.name || settings?.dept_name || 'Department';
  const hospitalName = hospital?.name || 'Hospital';
  const defaultProof: ProofNumber[] = [
    ...(procedures ? [{ value: `${procedures.length}+`, label: 'Procedures' }] : []),
    ...(testimonials ? [{ value: `${testimonials.total}+`, label: 'Patient Reviews' }] : []),
    ...(testimonials?.average_rating ? [{ value: `${testimonials.average_rating}★`, label: 'Rating' }] : []),
  ];
  const validProofNumbers = (proof_numbers || []).filter((p) => p && p.value);
  const proofItems = validProofNumbers.length > 0 ? validProofNumbers : defaultProof;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: C.bg, px: { xs: 2.5, md: '48px' }, pt: { xs: 8, md: '120px' }, pb: { xs: 6, md: '100px' } }}>
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.05) 1px, transparent 0)', backgroundSize: '40px 40px', opacity: 0.6 }} />
      <Box sx={{ position: 'absolute', top: '-10%', right: '-10%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(37, 99, 235,0.06) 0%, transparent 60%)', filter: 'blur(40px)' }} />

      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>
        <Box component={motion.div as any} initial="hidden" animate="visible" variants={stagger} sx={{ flex: 1 }}>
          <motion.div variants={fadeUp}><Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: '999px', px: '14px', py: '6px', mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}><Building2 size={14} color={C.text2} /><Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text2, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{hospitalName}</Typography></Box></motion.div>
          <motion.div variants={fadeUp}>
            <Typography variant="h1" sx={{ fontSize: { xs: 44, md: 68 }, fontWeight: 800, color: C.navy, lineHeight: 1.05, mb: 3, letterSpacing: '-0.03em' }}>
              Advanced <span style={{ background: `linear-gradient(135deg, ${C.teal} 0%, #06B6D4 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{deptName}.</span>
            </Typography>
          </motion.div>
          {/* Department Overview tab drives this copy: its tagline sits above the
              hero body text, and only then do we fall back to the hospital tagline. */}
          {settings?.tagline && (
            <motion.div variants={fadeUp}><Typography sx={{ fontSize: { xs: 18, md: 20 }, color: C.navy, fontWeight: 700, mb: 2, maxWidth: 480 }}>{settings.tagline}</Typography></motion.div>
          )}
          <motion.div variants={fadeUp}><Typography sx={{ fontSize: { xs: 16, md: 18 }, color: C.text2, fontWeight: 400, lineHeight: 1.6, mb: 5, maxWidth: 480 }}>{settings?.hero_body_text || hospital?.tagline || 'Experience world-class healthcare tailored to your distinct needs with our highly skilled specialists.'}</Typography></motion.div>
          <motion.div variants={fadeUp}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', bgcolor: C.navy, color: C.white, px: '28px', py: '16px', borderRadius: '16px', fontSize: 16, fontWeight: 700, textDecoration: 'none', transition: 'all 0.3s', boxShadow: `0 12px 24px -8px ${C.teal}`, '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 16px 32px -8px ${C.teal}`, bgcolor: C.teal } }}>
                {settings?.cta_button_text || 'Book Appointment'} <ArrowRight size={18} />
              </Box>
              {/* ── Video Consultation CTA ── */}
              <Box
                component="button"
                onClick={onVideoCall}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  bgcolor: 'transparent', color: C.teal,
                  border: `2px solid ${C.teal}`,
                  px: '24px', py: '14px', borderRadius: '16px', fontSize: 15, fontWeight: 700,
                  cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.3s',
                  '&:hover': { bgcolor: C.tealLt, transform: 'translateY(-2px)', boxShadow: `0 8px 20px -6px rgba(37,99,235,0.3)` },
                }}
              >
                <Video size={18} /> Video Consult
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ display: 'flex', '& > *': { ml: '-10px', border: `2px solid ${C.bg}`, borderRadius: '50%', width: 36, height: 36, bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, fontSize: 13, fontWeight: 700 } }}>
                  <Box sx={{ ml: 0, backgroundImage: 'url(https://i.pravatar.cc/100?img=1)', backgroundSize: 'cover' }} />
                  <Box sx={{ backgroundImage: 'url(https://i.pravatar.cc/100?img=2)', backgroundSize: 'cover' }} />
                  <Box sx={{ backgroundImage: 'url(https://i.pravatar.cc/100?img=3)', backgroundSize: 'cover' }} />
                  <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}` }}>+</Box>
                </Box>
                <Typography sx={{ fontSize: 13, color: C.text2, fontWeight: 600, lineHeight: 1.2 }}>Trusted by<br/><Box component="span" sx={{ color: C.navy }}>10,000+ patients</Box></Typography>
              </Box>
            </Box>
          </motion.div>
        </Box>
        <Box component={motion.div as any} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} sx={{ flex: 1, width: '100%' }}>
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: -20, background: 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(6,182,212,0.2) 100%)', filter: 'blur(30px)', borderRadius: '40px', zIndex: 0 }} />
            <Box className="glass-panel" sx={{ position: 'relative', zIndex: 1, borderRadius: '24px', p: 4, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4, border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.1)' }}>
              {proofItems.slice(0, 4).map((item, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 46 }, fontWeight: 800, color: C.navy, lineHeight: 1, display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    {(item.value || '').replace(/[^0-9.]/g, '')}
                    <Box component="span" sx={{ color: C.teal, fontSize: '0.6em' }}>{(item.value || '').replace(/[0-9.]/g, '')}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: C.text2, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AccreditationsSection({ items }: { items: AccredItem[] }) {
  const active = items.filter(a => a.active);
  if (active.length === 0) return null;
  return (
    <Box sx={{ bgcolor: C.white, py: '40px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ textAlign: 'center', fontSize: 13, letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 800, color: C.text3, mb: 5 }}>Recognized by Global Standards</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 3, md: 6 } }}>
          {active.map(a => (
            <motion.div key={a.id} variants={fadeUp}>
              <Box className="glass-panel" sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: 3, py: 2, borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.8)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.06)', bgcolor: C.white } }}>
                <Award size={28} color={C.teal} />
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, color: C.navy }}>{a.name}</Typography>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ProceduresSection({ procedures }: { procedures: ProcedureItem[] }) {
  if (!procedures || procedures.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Treatments</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Procedures & Specialties</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 3 }}>
          {procedures.map((p) => (
            <Box component={motion.div as any} variants={fadeUp} key={p.id} sx={{ position: 'relative', overflow: 'hidden', border: `1px solid ${C.border}`, borderRadius: '24px', p: '32px', bgcolor: C.white, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', cursor: 'pointer', '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 24px 48px -12px ${C.border}`, borderColor: 'rgba(37,99,235,0.3)', '& .proc-arrow': { transform: 'translateX(4px)', color: C.teal } }}}>
              {p.is_featured && (
                <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: C.navy, color: C.white, px: '10px', py: '4px', borderRadius: '8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Featured
                </Box>
              )}
              <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '24px', color: C.teal, transition: 'all 0.3s' }}>
                {getIcon(p.emoji || '')}
              </Box>
              <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 800, color: C.navy, mb: '10px', pr: 4 }}>{p.name}</Typography>
              {p.description && <Typography sx={{ fontSize: 15, color: C.text2, lineHeight: 1.6, mb: '24px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.description}</Typography>}
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', borderTop: `1px solid ${C.border}`, pt: 3 }}>
                {(p.min_price || p.max_price) ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: 11, color: C.text3, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Starting From</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{p.min_price ? `₹${p.min_price}` : ''} {p.min_price && p.max_price ? '-' : ''} {p.max_price ? `₹${p.max_price}` : ''}</Typography>
                  </Box>
                ) : <Box />}
                <ArrowRight className="proc-arrow" size={20} color={C.text3} style={{ transition: 'all 0.3s' }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function DoctorSection({ doctor }: { doctor: DoctorProfile }) {
  if (!doctor) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 6, md: 8 }, alignItems: 'center' }}>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1, position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: -15, background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(6,182,212,0.15) 100%)', borderRadius: '32px', zIndex: 0, transform: 'rotate(-3deg)' }} />
            <Box component="img" src={doctor.photo_url || 'https://via.placeholder.com/600'} sx={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', borderRadius: '24px', position: 'relative', zIndex: 1, boxShadow: '0 24px 48px -12px rgba(0,0,0,0.15)' }} />
            
            {/* Floating Badge */}
            <Box className="glass-panel" sx={{ position: 'absolute', bottom: -20, right: -20, zIndex: 10, bgcolor: 'rgba(255,255,255,0.9)', p: '16px 24px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 12px 32px rgba(0,0,0,0.1)' }}>
              <Box sx={{ width: 40, height: 40, bgcolor: C.goldLt, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Star size={20} color={C.gold} fill={C.gold} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.navy, lineHeight: 1 }}>{doctor.rating || '4.9'} <span style={{fontSize: 14, color: C.text3}}>/5</span></Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text2 }}>Highly Rated</Typography>
              </Box>
            </Box>
          </Box>
          <Box component={motion.div as any} variants={fadeUp} sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Lead Specialist</Typography>
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 32, md: 48 }, fontWeight: 800, color: C.navy, mb: '12px', lineHeight: 1.1 }}>{doctor.name}</Typography>
            <Typography sx={{ fontSize: 18, color: C.text2, mb: '8px' }}>{doctor.qualifications}</Typography>
            <Typography sx={{ fontSize: 16, color: C.teal, fontWeight: 600, mb: '32px' }}>{doctor.specialty}</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 3 }}>
              {[ { value: `${doctor.years_exp}+`, label: 'Years Exp' }, { value: doctor.surgeries, label: 'Surgeries' }, { value: `${doctor.rating}★`, label: 'Rating' } ].filter(s => s.value).map((stat, i) => (
                <Box key={i}>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 28, fontWeight: 800, color: C.navy }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: 12, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{stat.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function TestimonialsSection({ testimonials }: { testimonials: TestimonialSample[] }) {
  if (!testimonials || testimonials.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Testimonials</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>What Our Patients Say</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' }, gap: 3 }}>
          {testimonials.map((t, i) => (
            <Box component={motion.div as any} variants={fadeUp} key={i} sx={{ border: `1px solid ${C.border}`, borderRadius: '20px', p: '32px', bgcolor: C.white, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.08)' }}}>
              <MessageSquareQuote size={32} color={C.teal} style={{ marginBottom: '16px', opacity: 0.2 }} />
              <Typography sx={{ fontSize: 16, color: C.text, lineHeight: 1.6, mb: '24px', fontStyle: 'italic' }}>"{t.quote}"</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontWeight: 700 }}>
                  {t.name.charAt(0)}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.navy }}>{t.name}</Typography>
                  <Typography sx={{ fontSize: 13, color: C.text2 }}>{t.procedure || 'Patient'}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function TreatmentsSection({ items }: { items: ConditionTreatment[] }) {
  if (!items || items.length === 0) return null;
  // Group treatments by their speciality
  const groups: Record<string, ConditionTreatment[]> = {};
  items.forEach((it) => {
    const key = it.speciality_name || 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(it);
  });
  const groupKeys = Object.keys(groups);
  return (
    <Box sx={{ py: '80px', bgcolor: C.bg }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Our Expertise</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Treatments &amp; Procedures</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 3 }}>
          {groupKeys.map((key) => (
            <Box component={motion.div as any} variants={fadeUp} key={key} sx={{ border: `1px solid ${C.border}`, borderRadius: '24px', p: '28px', bgcolor: C.white, transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 20px 40px -12px ${C.border}`, borderColor: 'rgba(37,99,235,0.3)' } }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', color: C.teal }}>
                <Activity size={22} />
              </Box>
              <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 17, fontWeight: 800, color: C.navy, mb: '16px', textTransform: 'capitalize' }}>{key.toLowerCase()}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {groups[key].map((t) => (
                  <Box key={t.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.teal, flexShrink: 0, mt: '7px' }} />
                    <Typography sx={{ fontSize: 14, color: C.text2, fontWeight: 500, textTransform: 'capitalize' }}>{t.name.toLowerCase()}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function MediaSection({ items }: { items: MediaItem[] }) {
  const images = (items || []).filter((m) => (m.media_type || '').toUpperCase() === 'IMAGE');
  if (images.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Gallery</Typography>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>A Look Inside</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 3 }}>
          {images.map((m) => (
            <Box component={motion.div as any} variants={fadeUp} key={m.id} sx={{ position: 'relative', borderRadius: '20px', overflow: 'hidden', aspectRatio: '4/3', border: `1px solid ${C.border}`, bgcolor: C.bg }}>
              <Box component="img" src={mediaSrc(m.media_url)} alt={m.caption || 'Gallery image'} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s', '&:hover': { transform: 'scale(1.05)' } }} />
              {m.caption && (
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: 2, py: 1.5, background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.white }}>{m.caption}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FaqSection({ faqs }: { faqs: FaqItem[] }) {
  if (!faqs || faqs.length === 0) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ textAlign: 'center', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>FAQ</Typography>
        <Typography sx={{ textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 28, md: 40 }, fontWeight: 800, color: C.navy, mb: '40px' }}>Frequently Asked Questions</Typography>
        <Box>
          {faqs.map((f, i) => (
            <motion.div variants={fadeUp} key={f.id}>
              <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' }, borderBottom: `1px solid ${C.border}` }}>
                <AccordionSummary expandIcon={<ChevronDown size={20} color={C.text2} />} sx={{ px: 0, py: 2 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.navy }}>{f.question}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pb: 3 }}>
                  <Typography sx={{ fontSize: 15, color: C.text2, lineHeight: 1.6 }}>{f.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CareCoordinatorSection({ coordinator, whatsappUrl }: { coordinator: CareCoordinator; whatsappUrl: string }) {
  if (!coordinator || !coordinator.name) return null;
  return (
    <Box sx={{ py: '80px', bgcolor: C.white, borderTop: `1px solid ${C.border}` }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10%" }} variants={stagger} sx={{ maxWidth: 800, mx: 'auto', px: { xs: 3, md: '48px' } }}>
        <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: C.teal, mb: '12px' }}>Your Care Coordinator</Typography>
        <Box component={motion.div as any} variants={fadeUp} sx={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap', border: `1px solid ${C.border}`, borderRadius: '24px', p: '32px', bgcolor: C.bg }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: C.tealLt, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0 }}>
            {coordinator.emoji || '🧑‍💼'}
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 22, fontWeight: 800, color: C.navy }}>{coordinator.name}</Typography>
            {coordinator.role && <Typography sx={{ fontSize: 14, color: C.text2, mt: '4px' }}>{coordinator.role}</Typography>}
            {coordinator.availability && <Typography sx={{ fontSize: 13, color: C.text3, mt: '6px' }}>{coordinator.availability}</Typography>}
          </Box>
          <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', bgcolor: C.navy, color: C.white, px: '24px', py: '14px', borderRadius: '14px', fontSize: 15, fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s', '&:hover': { bgcolor: C.teal } }}>
            Talk to {coordinator.name.split(' ')[0]} <ArrowRight size={18} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FinalSection({ whatsappUrl }: { whatsappUrl: string }) {
  return (
    <Box sx={{ py: '100px', bgcolor: C.navy, textAlign: 'center', px: 3 }}>
      <Box component={motion.div as any} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} sx={{ maxWidth: 640, mx: 'auto' }}>
        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: { xs: 36, md: 52 }, fontWeight: 800, color: C.white, mb: '24px', lineHeight: 1.1 }}>Ready for better care?</Typography>
        <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', mb: '40px' }}>Our specialists are here to design a personalized treatment plan for you.</Typography>
        <Box component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', bgcolor: C.white, color: C.navy, px: '32px', py: '16px', borderRadius: '16px', fontSize: 16, fontWeight: 700, textDecoration: 'none', transition: 'all 0.2s', '&:hover': { transform: 'scale(1.02)' } }}>
          Book Your Consultation <ArrowRight size={20} />
        </Box>
      </Box>
    </Box>
  );
}

export default function DeptLinkTemplate({ initialData }: { initialData?: DeptTemplateData }) {
  const { deptId } = useParams<{ deptId?: string }>();
  const [data, setData] = useState<DeptTemplateData | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    if (initialData) return;
    if (!deptId) return;
    hospitalApiService.getDeptPreview(Number(deptId)).then((res: any) => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [deptId, initialData]);

  if (loading) return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: C.bg }}>{fontStyles}<CircularProgress sx={{ color: C.teal }} /></Box>;
  if (!data) return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: C.bg }}>{fontStyles}<Typography variant="h5">Link not found</Typography></Box>;

  const whatsappUrl = data.settings?.whatsapp_number ? `https://wa.me/${data.settings.whatsapp_number.replace(/\D/g, '')}` : '#';

  const testimonialList = data.all_testimonials || data.testimonial_list || data.testimonials?.samples || data.testimonials?.items || data.testimonials?.list || [];
  const faqList = data.faq_list || data.faqs?.items || data.faqs?.faq_list || data.faqs?.list || [];
  // Section Visibility switches from the department's Overview tab.
  const toggles = data.visibility_toggles || {};

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
      {fontStyles}
      <TrustBar />
      <HeroSection data={data} whatsappUrl={whatsappUrl} onVideoCall={() => setVideoOpen(true)} />
      {toggles.inherit_accreditations !== false && <AccreditationsSection items={data.trust_signals?.accreditations || []} />}
      <ProceduresSection procedures={data.procedures || []} />
      <TreatmentsSection items={data.conditions_treatments || []} />
      {data.featured_doctor && <DoctorSection doctor={data.featured_doctor} />}
      <TestimonialsSection testimonials={testimonialList} />
      <MediaSection items={data.media?.items || []} />
      {data.care_coordinator && <CareCoordinatorSection coordinator={data.care_coordinator} whatsappUrl={whatsappUrl} />}
      <FaqSection faqs={faqList} />
      <FinalSection whatsappUrl={whatsappUrl} />
      {/* ── Whereby Video Consultation Modal ── */}
      <VideoConsultModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        deptId={deptId}
        context="dept-consult"
        hospitalName={data.hospital?.name}
      />
    </Box>
  );
}
